import 'rxjs/add/operator/switchMap';
import { type ActionsObservable } from 'redux-observable';
import { of } from 'rxjs/observable/of';

import { fetchFailed, fetchSuccess } from '../../common/fetch-request-analytics';
import {
	CHECK_CONFLUENCE_ACCESS_WITH_RETRY,
	checkProductsLicenceState,
	fetchConfluenceAccessRequestCapabilities,
	inlineJoinConfluenceFailed,
	resetConnectedSpace,
	updateConfluenceUserAccess,
} from '../actions';
import { fetchPersonalizationSiteTraits } from '../actions/personalization-site-traits';

import { fetchConfluenceCurrentUser } from './requests';
import { mergeMap } from 'rxjs/operators/mergeMap';
import { catchError } from 'rxjs/operators/catchError';
import { retryWhen } from 'rxjs/operators/retryWhen';
import { takeWhile } from 'rxjs/operators/takeWhile';
import { delay } from 'rxjs/operators/delay';
import { UNKNOWN } from '../confluence/user/types';
import get from 'lodash/get';
import { from } from 'rxjs/observable/from';
import { NO_ACCESS_ERROR_MESSAGE } from './inline-join';

export default (action$: ActionsObservable<any>) =>
	action$.ofType(CHECK_CONFLUENCE_ACCESS_WITH_RETRY).switchMap(() => {
		// In staging this takes roughly 10 seconds to resolve, 15 adds some overhead to 10 retries without making it too long
		// for users.
		const maxTries = 15;
		let retries = maxTries - 1;

		return fetchConfluenceCurrentUser().pipe(
			mergeMap((value) => {
				fetchSuccess('confluence.user.retry');
				if (get(value, 'operations.length')) {
					return from([
						updateConfluenceUserAccess(UNKNOWN),
						resetConnectedSpace(),
						/* 
							The most important observables to trigger when we got access to Confluence are:
								- checkProductsLicenceState
								- fetchPersonalizationSiteTraits
							without them the screen is going to be stuck in connectedSpace.connectionState === "LOADING"
						*/
						checkProductsLicenceState(),
						fetchPersonalizationSiteTraits(),
						fetchConfluenceAccessRequestCapabilities(),
					]);
				}

				throw new Error(NO_ACCESS_ERROR_MESSAGE);
			}),
			retryWhen((errors) => {
				return errors.pipe(
					takeWhile((error) => {
						if (error.statusCode !== 403) {
							throw error;
						}
						if (retries <= 0) {
							throw new Error(NO_ACCESS_ERROR_MESSAGE + ` after ${maxTries} tries`);
						}
						retries -= 1;
						return true;
					}),
					delay(1000),
				);
			}),
			catchError((err) => {
				if (err.message !== NO_ACCESS_ERROR_MESSAGE + ` after ${maxTries} tries`) {
					fetchFailed('confluence.user.retry', err);
				}
				return of(inlineJoinConfluenceFailed());
			}),
		);
	});
